import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@/components/Loading';
import { ScoreDisplay } from '@/components/ScoreDisplay';
import { useGameEngine } from '@/hooks/useGameEngine';
import { useAuthStore } from '@/store/authStore';
import { useBgmStore } from '@/store/bgmStore';
import { useGameStore } from '@/store/gameStore';
import { useSeStore } from '@/store/seStore';

function GamePage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { setScore, resetGame, saveScore, setPendingSave, clearPendingSave } =
    useGameStore();
  const { play, pause, isPlaying, isBgmEnabled } = useBgmStore();
  const [canStart, setCanStart] = useState(false);

  const { engineRef: gameEngineRef, isLoading } = useGameEngine({
    containerRef,
    isEnabled: canStart,
    enableBlockMerge: true,
    onGameEnd: (finalScore, blockCounts) => {
      setScore(finalScore);
      saveScore();
      setPendingSave({ finalScore, blockCounts });
      navigate('/results');
    },
  });

  useEffect(() => {
    const handler = () => {
      gameEngineRef.current?.endGame();
    };
    window.addEventListener('requestGameEnd', handler);
    return () => {
      window.removeEventListener('requestGameEnd', handler);
    };
  }, [gameEngineRef]);

  const initGame = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;

    const userId = await useAuthStore.getState().ensureValidUserId();
    if (!userId) {
      navigate('/', { replace: true });
      setCanStart(false);
      return;
    }

    resetGame();
    clearPendingSave();
    setCanStart(true);

    const { seVolume, setSeVolume } = useSeStore.getState();
    setSeVolume(seVolume);
  }, [navigate, resetGame, clearPendingSave]);

  useEffect(() => {
    initGame();
    return () => {
      setCanStart(false);
    };
  }, [initGame]);

  useEffect(() => {
    if (!isBgmEnabled) {
      pause();
      return;
    }

    if (!isPlaying()) {
      play(2000);
    }
  }, [isBgmEnabled, pause, play, isPlaying]);

  return (
    <div className="h-svh overflow-clip bg-linear-to-br from-gray-900 to-gray-800 flex items-center justify-center">
      {isLoading && <Loading />}
      <div className="relative">
        <ScoreDisplay />
        <div
          ref={containerRef}
          id="game-container"
          className="touch-none select-none grid place-items-center md:h-full [&>canvas]:aspect-4/7 [&>canvas]:w-[90%] md:[&>canvas]:w-full"
        />
      </div>
    </div>
  );
}

export default GamePage;
